import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { DevelopmentalHistoryFormValues } from "./interface";
import useGetDevelopmentalHistory from "hooks/api/client/useGetDevelopmentalHistory";
import useUpsertDevelopmentalHistory from "hooks/api/client/useUpsertDevelopmentalHistory";

const EMPTY_VALUES: DevelopmentalHistoryFormValues = {
  whenDevelopmentSeemedDifferent: "",
  complicationsPregnancyBirth: "",
  ageBeganCrawlingMonths: null,
  ageBeganWalkingMonths: null,
  ageBeganSpeakingMonths: null,
  childTemperament: "",
  interestedInBeingHeld: "",
  interestedInBeingPlayedWith: "",
  caregiverAttendedToNeeds: "",
  additionalNotesInfancyChildhood: "",
};

export const DevelopmentalHistory: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { developmentalHistory, refetchDevelopmentalHistory } =
    useGetDevelopmentalHistory(clientIdNum);

  const { upsertDevelopmentalHistoryMutation, isUpsertDevelopmentalHistoryLoading } =
    useUpsertDevelopmentalHistory({
      onSuccess: () => {
        refetchDevelopmentalHistory();
        setEditMode(false);
      },
    });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<DevelopmentalHistoryFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<DevelopmentalHistoryFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (developmentalHistory) {
      // Unanswered text/select fields come back as `null` from the API; coerce
      // to "" so controlled inputs never receive a null value.
      const normalized: DevelopmentalHistoryFormValues = {
        ...developmentalHistory,
        whenDevelopmentSeemedDifferent: developmentalHistory.whenDevelopmentSeemedDifferent ?? "",
        complicationsPregnancyBirth: developmentalHistory.complicationsPregnancyBirth ?? "",
        childTemperament: developmentalHistory.childTemperament ?? "",
        interestedInBeingHeld: developmentalHistory.interestedInBeingHeld ?? "",
        interestedInBeingPlayedWith: developmentalHistory.interestedInBeingPlayedWith ?? "",
        caregiverAttendedToNeeds: developmentalHistory.caregiverAttendedToNeeds ?? "",
        additionalNotesInfancyChildhood: developmentalHistory.additionalNotesInfancyChildhood ?? "",
      };
      setFormValues(normalized);
      setInitialValues(normalized);
    }
  }, [developmentalHistory]);

  const handleChange =
    (field: keyof DevelopmentalHistoryFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    const payload = {
      ...formValues,
      whenDevelopmentSeemedDifferent: formValues.whenDevelopmentSeemedDifferent || null,
      ageBeganCrawlingMonths: formValues.ageBeganCrawlingMonths !== null ? Number(formValues.ageBeganCrawlingMonths) : null,
      ageBeganWalkingMonths: formValues.ageBeganWalkingMonths !== null ? Number(formValues.ageBeganWalkingMonths) : null,
      ageBeganSpeakingMonths: formValues.ageBeganSpeakingMonths !== null ? Number(formValues.ageBeganSpeakingMonths) : null,
      childTemperament: formValues.childTemperament || null,
      interestedInBeingHeld: formValues.interestedInBeingHeld || null,
      interestedInBeingPlayedWith: formValues.interestedInBeingPlayedWith || null,
      caregiverAttendedToNeeds: formValues.caregiverAttendedToNeeds || null,
    };
    upsertDevelopmentalHistoryMutation({ clientId: clientIdNum, payload });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isUpsertDevelopmentalHistoryLoading;

  return (
    <SectionWrapper
      title="Developmental History"
      description="Key milestones and early developmental patterns that help inform care planning and treatment approach."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default DevelopmentalHistory;
